import React, { useEffect, useState } from "react";
import {
  login,
  register,
  forgotPassword,
  resetPassword,
  verifyEmail
} from "../auth";

function readHashToken() {
  // Supports #/reset-password?token=xxx and #/verify-email?token=xxx
  const hash = window.location.hash || "";
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return "";
  const params = new URLSearchParams(hash.slice(qIndex + 1));
  return params.get("token") || "";
}

function EyeIcon({ open }) {
  return open ? (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

function initialMode() {
  const hash = (window.location.hash || "").toLowerCase();
  if (hash.includes("reset-password")) return "reset";
  if (hash.includes("verify-email")) return "verify";
  if (hash.includes("forgot-password")) return "forgot";
  if (hash.includes("register")) return "register";
  return "login";
}

export default function AuthScreen({ onAuthenticated, darkMode = true }) {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [token, setToken] = useState(readHashToken);

  useEffect(() => {
    if (mode !== "verify") return;
    const tok = readHashToken();
    if (!tok) {
      setError("No verification token found.");
      return;
    }
    let active = true;
    setBusy(true);
    verifyEmail(tok)
      .then(() => {
        if (active) setNotice("Your email has been verified. You can now log in.");
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setBusy(false);
      });
    return () => {
      active = false;
    };
  }, [mode]);

  function resetFeedback() {
    setError("");
    setNotice("");
  }

  function switchMode(next) {
    resetFeedback();
    setPassword("");
    setConfirm("");
    setMode(next);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    resetFeedback();

    try {
      setBusy(true);
      if (mode === "login") {
        await login(email, password);
        onAuthenticated && onAuthenticated();
      } else if (mode === "register") {
        if (password.length < 6) throw new Error("Password must be at least 6 characters.");
        if (password !== confirm) throw new Error("Passwords do not match.");
        await register(name, email, password);
        onAuthenticated && onAuthenticated();
      } else if (mode === "forgot") {
        const res = await forgotPassword(email);
        setNotice(res.message || "If that email exists, a reset link has been sent.");
      } else if (mode === "reset") {
        if (password.length < 6) throw new Error("Password must be at least 6 characters.");
        if (password !== confirm) throw new Error("Passwords do not match.");
        const res = await resetPassword(token, password);
        setNotice(res.message || "Password updated. You can now log in.");
        setMode("login");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const titles = {
    login: "Welcome back",
    register: "Create your account",
    forgot: "Reset your password",
    reset: "Set a new password",
    verify: "Email verification"
  };
  const subtitles = {
    login: "Log in to your SendX dashboard",
    register: "Start managing your email campaigns",
    forgot: "Enter your email and we'll send you a reset link",
    reset: "Choose a new password for your account",
    verify: "Confirming your email address"
  };

  return (
    <div className={darkMode ? "sx-auth sx-dark" : "sx-auth sx-light"}>
      <div className="sx-auth-card">
        <div className="sx-auth-brand">
          <span className="sx-auth-logo">✈</span>
          <span className="sx-auth-brand-name">SendX</span>
        </div>
        <h1 className="sx-auth-title">{titles[mode]}</h1>
        <p className="sx-auth-sub">{subtitles[mode]}</p>

        {error && <div className="sx-auth-alert sx-auth-error">{error}</div>}
        {notice && <div className="sx-auth-alert sx-auth-notice">{notice}</div>}

        {mode === "verify" ? (
          <div className="sx-auth-verify">
            {busy && <p>Verifying...</p>}
            <button type="button" className="sx-auth-btn" onClick={() => switchMode("login")}>
              Go to login
            </button>
          </div>
        ) : (
          <form className="sx-auth-form" onSubmit={handleSubmit}>
            {mode === "register" && (
              <label className="sx-auth-field">
                <span>Full name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                />
              </label>
            )}

            {(mode === "login" || mode === "register" || mode === "forgot") && (
              <label className="sx-auth-field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  required
                />
              </label>
            )}

            {(mode === "login" || mode === "register" || mode === "reset") && (
              <label className="sx-auth-field">
                <span>Password</span>
                <div className="sx-auth-pass-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    required
                  />
                  <button
                    type="button"
                    className="sx-auth-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <EyeIcon open={showPassword} />
                  </button>
                </div>
              </label>
            )}

            {(mode === "register" || mode === "reset") && (
              <label className="sx-auth-field">
                <span>Confirm password</span>
                <div className="sx-auth-pass-wrap">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="sx-auth-eye"
                    onClick={() => setShowConfirm((v) => !v)}
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                    title={showConfirm ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
              </label>
            )}

            {mode === "login" && (
              <div className="sx-auth-row-end">
                <button type="button" className="sx-auth-link" onClick={() => switchMode("forgot")}>
                  Forgot password?
                </button>
              </div>
            )}

            <button type="submit" className="sx-auth-btn" disabled={busy}>
              {busy
                ? "Please wait..."
                : mode === "login"
                ? "Log in"
                : mode === "register"
                ? "Create account"
                : mode === "forgot"
                ? "Send reset link"
                : "Update password"}
            </button>
          </form>
        )}

        <div className="sx-auth-footer">
          {mode === "login" && (
            <p>
              Don't have an account?{" "}
              <button type="button" className="sx-auth-link" onClick={() => switchMode("register")}>
                Sign up
              </button>
            </p>
          )}
          {mode === "register" && (
            <p>
              Already have an account?{" "}
              <button type="button" className="sx-auth-link" onClick={() => switchMode("login")}>
                Log in
              </button>
            </p>
          )}
          {(mode === "forgot" || mode === "reset") && (
            <p>
              <button type="button" className="sx-auth-link" onClick={() => switchMode("login")}>
                Back to login
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
